import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Flame, X } from "lucide-react";
import Card from "../components/ui/Card";
import DebouncedNotes from "../components/ui/DebouncedNotes";
import { PRAYERS, SLIP_REASONS } from "../lib/constants";
import { useAppState } from "../context/AppStateContext";

function formatStreak(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function HomePage({ goTo }) {
  const {
    state,
    today,
    masked,
    actions,
    lastDurationMs,
    bestDurationMs,
  } = useAppState();

  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [slipNotes, setSlipNotes] = useState("");
  const [slipError, setSlipError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force re-render every second to update live streak timer
  const [, forceUpdate] = useState();
  
  useEffect(() => {
    if (!state.streakActive) return;
    const id = setInterval(() => {
      forceUpdate({});
    }, 1000);
    return () => clearInterval(id);
  }, [state.streakActive]);

  // Calculate streak duration on-demand without adding dependency
  const liveStreakDurationMs = (() => {
    if (!state.streakActive || !state.streakStartAt) return 0;
    
    const startTime = typeof state.streakStartAt === 'number'
      ? state.streakStartAt
      : new Date(state.streakStartAt).getTime();
    
    if (isNaN(startTime)) {
      console.error('Invalid streak start time:', state.streakStartAt);
      return 0;
    }
    
    return Math.max(0, Date.now() - startTime);
  })();

  const todaysPrayers = state.prayerLog[today] || {};
  const prayersDoneCount = PRAYERS.reduce((acc, p) => acc + (todaysPrayers[p] ? 1 : 0), 0);

  const streakLabel = state.streakActive
    ? "current streak"
    : state.history?.length
    ? "last streak"
    : "streak";

  const streakValueMs = state.streakActive ? liveStreakDurationMs : lastDurationMs;

  const statusText = useMemo(() => {
    if (state.streakActive) {
      return "You're on track. Keep it simple: change environment, breathe, and do a small good action.";
    }
    if (state.history?.length) {
      const last = state.history[state.history.length - 1];
      return `Last streak ended. You lasted ${formatStreak(last?.durationMs || 0)}. Reflect briefly, then start again.`;
    }
    return "Press Start when you're ready to begin. If you slip, end the streak and we'll save your stats.";
  }, [state.streakActive, state.history]);

  const handleSlipConfirm = async () => {
    if (!state.streakActive) {
      setSlipError("No active streak to end. Please start one first.");
      return;
    }

    setIsSubmitting(true);
    setSlipError("");
    
    try {
      await actions.endStreak({
        slipReasons: selectedReasons,
        slipNotes: slipNotes,
      });

      setShowSlipModal(false);
      setSelectedReasons([]);
      setSlipNotes("");
      setSlipError("");
    } catch (error) {
      console.error("Error ending streak:", error);
      setSlipError("Failed to save streak data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
      {/* Emergency Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => goTo("tempted")}
        aria-label="Get immediate help - open coping strategies page"
        className="w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-4 px-4 font-bold text-lg shadow-lg hover:shadow-xl transition-all ring-2 ring-amber-400/50"
      >
        I'm Tempted - Get Help Now
      </motion.button>

      {/* Streak Card */}
      <Card
        title="Your streak"
        icon={Flame}
        right={
          <div className="text-right">
            <div className="text-2xl font-bold text-[#10B981] glow-mint">{formatStreak(streakValueMs)}</div>
            <div className="text-xs text-zinc-400">{streakLabel}</div>
          </div>
        }
      >
        <div className="text-sm text-zinc-400">{statusText}</div>

        <div className="mt-3 text-xs text-zinc-600">
          Best:{" "}
          <span className="font-semibold text-[#10B981]">
            {formatStreak(bestDurationMs)}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={actions.startStreak}
            aria-label={state.streakActive ? "Streak already started" : "Start a new streak"}
            className={
              "rounded-xl py-3 font-semibold ring-1 transition-all active:scale-95 min-h-[44px] " +
              (state.streakActive
                ? "bg-zinc-800 text-zinc-400 cursor-not-allowed ring-zinc-800"
                : "bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300")
            }
            disabled={state.streakActive}
          >
            {state.streakActive ? "Started" : state.history?.length ? "Start again" : "Start"}
          </button>

          <button
            onClick={() => {
              if (!state.streakActive) return;
              setShowSlipModal(true);
            }}
            aria-label="Report a slip and end current streak"
            className={
              "rounded-xl py-3 font-semibold ring-1 transition-all " +
              (state.streakActive
                ? "bg-[#10B981] text-zinc-900 hover:bg-emerald-600 ring-[#10B981]/50 glow-mint"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed ring-zinc-800")
            }
            disabled={!state.streakActive}
          >
            {masked ? "I slipped" : "End streak"}
          </button>
        </div>
      </Card>

      {/* Today's Prayers Card */}
      <Card title="Today's prayers" icon={CheckCircle2} right={<div className="text-sm font-semibold text-[#10B981]">{prayersDoneCount}/5</div>}>
        <div className="grid grid-cols-2 gap-2">
          {PRAYERS.map((p) => (
            <button
              key={p}
              onClick={() => actions.togglePrayer(today, p)}
              className={
                "rounded-xl py-3 ring-1 text-sm font-semibold transition-all active:scale-95 min-h-[44px] " +
                (todaysPrayers[p]
                  ? "bg-[#10B981] text-zinc-900 ring-[#10B981] glow-mint hover:bg-emerald-600"
                  : "bg-zinc-900/50 text-zinc-400 ring-zinc-800 hover:bg-zinc-800")
              }
            >
              {p}
            </button>
          ))}
        </div>

        <div className="mt-3">
          <DebouncedNotes
            value={state.notes[today] || ""}
            onChange={(value) => actions.setNote(today, value)}
          />
        </div>
      </Card>

      {/* Slip Modal */}
      {showSlipModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-end z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="slip-modal-title"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowSlipModal(false);
              setSelectedReasons([]);
              setSlipNotes("");
              setSlipError("");
            }
          }}
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            className="w-full bg-[#18181B] border-t border-zinc-800 rounded-t-2xl p-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="slip-modal-title" className="text-lg font-bold text-white">What led to the slip?</h2>
              <button
                onClick={() => {
                  setShowSlipModal(false);
                  setSelectedReasons([]);
                  setSlipNotes("");
                  setSlipError("");
                }}
                aria-label="Close dialog"
                className="p-2 hover:bg-zinc-800 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>

            {slipError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-200 text-sm">
                {slipError}
              </div>
            )}
            
            <div className="text-sm text-zinc-600 mb-4">
              No judgment. Understanding your triggers helps us support you better.
            </div>

            <div className="space-y-2 mb-4">
              {SLIP_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => {
                    if (selectedReasons.includes(reason)) {
                      setSelectedReasons(selectedReasons.filter((r) => r !== reason));
                    } else {
                      setSelectedReasons([...selectedReasons, reason]);
                    }
                  }}
                  className={
                    "w-full text-left p-3 rounded-xl ring-1 transition-all " +
                    (selectedReasons.includes(reason)
                      ? "bg-[#10B981] text-zinc-900 ring-[#10B981] glow-mint"
                      : "bg-zinc-900/50 text-zinc-400 ring-zinc-800 hover:bg-zinc-800")
                  }
                >
                  {reason}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <label className="text-xs text-zinc-600 uppercase tracking-widest font-semibold mb-2 block">Additional details (optional)</label>
              <textarea
                value={slipNotes}
                onChange={(e) => setSlipNotes(e.target.value)}
                placeholder="What happened, how you felt, what you need next…"
                className="w-full rounded-xl bg-[#09090B] border border-zinc-800 text-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#10B981]"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setShowSlipModal(false);
                  setSelectedReasons([]);
                  setSlipNotes("");
                }}
                className="rounded-xl py-3 font-semibold bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSlipConfirm}
                disabled={isSubmitting}
                className={`rounded-xl py-3 font-semibold ring-1 transition-all min-h-[44px] ${
                  isSubmitting
                    ? "bg-zinc-800 text-zinc-500 cursor-not-allowed ring-zinc-800"
                    : "bg-[#10B981] text-zinc-900 ring-[#10B981]/50 glow-mint hover:bg-emerald-600"
                }`}
              >
                {isSubmitting ? "Saving..." : "End streak"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
